import { addDays, startOfDay } from 'date-fns';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { PlannerView } from '~/components/PlannerView';
import type { PlannerViewData as _PlannerViewData } from '~/components/layout/CalendarLayout';
import {
  CalendarLayout,
  useCalendarContext,
} from '~/components/layout/CalendarLayout';
import { useBreakpoint } from '~/hooks/useBreakpoint';
export type PlannerViewData = _PlannerViewData;

// Types that need to be exported
export type ZoomLevel = 'month' | 'default' | 'week';
export type FilteredScheduleEvent =
  PlannerViewData[number]['calendar'][number] & {
    filtered?: boolean;
  };
export type FilteredPlannerViewData = Array<
  Omit<PlannerViewData[number], 'calendar'> & {
    calendar: FilteredScheduleEvent[];
    top: boolean;
  }
>;

const ZOOM_LEVELS = ['month', 'default', 'week'] as const;

type HorizontalCalendarProps = {
  selectedJobId?: string;
  scrollDate: Date;
};

// Horizontal Calendar View Component - uses Context to get data
const HorizontalCalendarComponent: React.FC<HorizontalCalendarProps> = ({
  selectedJobId,
  scrollDate: externalScrollDate,
}) => {
  // Get data from Context including range extension functions
  const {
    data,
    stableData,
    filtersValue,
    setFiltersValue,
    isLoading,
    ensureDateInRange,
  } = useCalendarContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const [scrollDate, setScrollDate] = useState<Date>(externalScrollDate);
  // Track the current visible date from scrolling (ref to avoid re-renders and feedback loops)
  // This is the date the user has scrolled to - used for relative navigation
  const currentVisibleDateRef = useRef<Date>(externalScrollDate);
  // Sync external scrollDate (from header nav) into local state
  useEffect(() => {
    setScrollDate(externalScrollDate);
  }, [externalScrollDate]);
  const [previousScrollDate, setPreviousScrollDate] = useState<Date | null>(
    null
  );
  const [mobileInitialized, setMobileInitialized] = useState<boolean>(false);
  const [hasHadJobOpen, setHasHadJobOpen] = useState<boolean>(false);
  const [zoomIndex, setZoomIndex] = useState(1);

  const zoomLevel = ZOOM_LEVELS[zoomIndex];
  const canZoomOut = zoomIndex > 0;
  const canZoomIn = zoomIndex < ZOOM_LEVELS.length - 1;
  const handleZoomOut = useCallback(
    () => setZoomIndex((i) => (i > 0 ? i - 1 : i)),
    []
  );
  const handleZoomIn = useCallback(
    () => setZoomIndex((i) => (i < ZOOM_LEVELS.length - 1 ? i + 1 : i)),
    []
  );

  // Memoize job click handler
  const handleJobClick = useCallback(
    (jobId: string) => {
      // Preserve original returnTo when navigating from one job to another
      // This ensures closing only requires one click instead of two
      const currentState = location.state as { returnTo?: string } | null;
      const originalReturnTo = currentState?.returnTo;

      // If we're already in a job view, preserve the original returnTo
      // Otherwise, set returnTo to the current planner path
      const returnTo = originalReturnTo || '/jobs/planner';

      navigate(jobId, { state: { returnTo } });
    },
    [navigate, location.state]
  );

  // Date navigation helpers
  // Navigate relative to current visible date (from scroll position), not the scrollDate state
  const today = useCallback(() => {
    // Always create a new Date object with millisecond offset to ensure React detects the change
    const todayDate = startOfDay(new Date());
    todayDate.setMilliseconds(todayDate.getMilliseconds() + (Date.now() % 100));
    currentVisibleDateRef.current = todayDate;
    setScrollDate(todayDate);
  }, []);
  const prev = useCallback(() => {
    // Navigate 7 days before the current visible position (where user has scrolled to)
    const newDate = addDays(currentVisibleDateRef.current, -7);
    currentVisibleDateRef.current = newDate;
    setScrollDate(newDate);
  }, []);
  const next = useCallback(() => {
    // Navigate 7 days after the current visible position (where user has scrolled to)
    const newDate = addDays(currentVisibleDateRef.current, 7);
    currentVisibleDateRef.current = newDate;
    setScrollDate(newDate);
  }, []);

  // Memoize allJobs for effect dependencies
  const allJobs = useMemo(
    () =>
      stableData?.sites?.flatMap(
        (site: PlannerViewData[number]) => site.calendar
      ) ?? [],
    [stableData?.sites]
  );

  // Job navigation effects
  const [lastJobId, setLastJobId] = useState<string | undefined>(selectedJobId);

  useEffect(() => {
    if (selectedJobId !== lastJobId) {
      setLastJobId(selectedJobId);

      const selectedJob = allJobs.find(
        (job: PlannerViewData[number]['calendar'][number]) =>
          job.id === selectedJobId
      );
      const selectedJobDate = selectedJob?.start
        ? new Date(selectedJob?.start)
        : selectedJob?.end
        ? new Date(selectedJob?.end)
        : null;

      if (selectedJobId && selectedJobDate && !isMobile) {
        setPreviousScrollDate(scrollDate);
        setHasHadJobOpen(true);
      } else if (!selectedJobId && previousScrollDate && !isMobile) {
        setScrollDate(previousScrollDate);
        setPreviousScrollDate(null);
      } else if (
        !selectedJobId &&
        !location.pathname.includes('/new') &&
        !isMobile
      ) {
        setScrollDate(new Date());
      }

      if (selectedJobId && isMobile) {
        setHasHadJobOpen(true);
      }
    }
  }, [
    selectedJobId,
    allJobs,
    lastJobId,
    isMobile,
    location.pathname,
    scrollDate,
    previousScrollDate,
  ]);

  // Mobile initialization
  useEffect(() => {
    if (
      isMobile &&
      !selectedJobId &&
      !location.pathname.includes('/new') &&
      !mobileInitialized &&
      !hasHadJobOpen
    ) {
      setScrollDate(new Date());
      setMobileInitialized(true);
    }
  }, [
    isMobile,
    selectedJobId,
    location.pathname,
    mobileInitialized,
    hasHadJobOpen,
  ]);

  useEffect(() => {
    setMobileInitialized(false);
  }, [isMobile]);

  // Handle visible date changes from PlannerView to trigger range loading
  // This is called when the user scrolls and the center visible date changes
  // ensureDateInRange handles both incremental extensions and large jumps efficiently
  // Updates the ref (not state) so navigation arrows work from the current scroll position
  const handleVisibleDateChange = useCallback(
    (visibleDate: Date) => {
      ensureDateInRange?.(visibleDate);
      // Update the ref to track current visible position for arrow navigation
      // Using a ref instead of state avoids feedback loops (scrollDate change -> scroll -> change)
      currentVisibleDateRef.current = visibleDate;
    },
    [ensureDateInRange]
  );

  // Handlers for toggling planner view options
  const handleToggleShowTimes = useCallback(() => {
    if (!filtersValue || !setFiltersValue) return;
    const newFilters = new URLSearchParams(filtersValue.toString());
    const currentValue = newFilters.get('jobDetail');
    if (currentValue) {
      // jobDetail param exists: remove it to hide times
      newFilters.delete('jobDetail');
    } else {
      // jobDetail param missing: set it to show times
      newFilters.set('jobDetail', 'true');
    }
    setFiltersValue(newFilters);
  }, [filtersValue, setFiltersValue]);

  const handleToggleShowBookings = useCallback(() => {
    if (!filtersValue || !setFiltersValue) return;
    const newFilters = new URLSearchParams(filtersValue.toString());
    const currentValue = newFilters.get('bookings');
    if (currentValue) {
      // bookings param exists: remove it to hide bookings
      newFilters.delete('bookings');
    } else {
      // bookings param missing: set it to show bookings
      newFilters.set('bookings', 'true');
    }
    setFiltersValue(newFilters);
  }, [filtersValue, setFiltersValue]);

  return (
    <>
      <PlannerView
        scrollDate={scrollDate}
        selectedJobId={selectedJobId}
        data={data ?? []}
        onClick={handleJobClick}
        jobDetail={!filtersValue?.get('jobDetail')}
        bookingsHidden={!!filtersValue?.get('bookings')}
        zoomLevel={zoomLevel}
        isLoading={isLoading ?? false}
        originalDataLength={stableData?.sites?.length || 0}
        onNavigatePrev={prev}
        onNavigateNext={next}
        onNavigateToday={today}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
        onVisibleDateChange={handleVisibleDateChange}
        onToggleShowTimes={handleToggleShowTimes}
        onToggleShowBookings={handleToggleShowBookings}
      />
    </>
  );
};

HorizontalCalendarComponent.propTypes = {
  selectedJobId: PropTypes.string,
  scrollDate: PropTypes.instanceOf(Date).isRequired,
};

const HorizontalCalendar = React.memo(HorizontalCalendarComponent);

HorizontalCalendar.displayName = 'HorizontalCalendar';

/* function ZoomControls({
  zoomIndex,
  setZoomIndex,
}: {
  zoomIndex: number;
  setZoomIndex: (index: number) => void;
}) {
  const [canZoomOut, canZoomIn] = [
    zoomIndex > 0,
    zoomIndex < ZOOM_LEVELS.length - 1,
  ];
  const [onZoomOut, onZoomIn] = [
    canZoomOut ? () => setZoomIndex(zoomIndex - 1) : undefined,
    canZoomIn ? () => setZoomIndex(zoomIndex + 1) : undefined,
  ];

  const ZoomButton = ({
    disabled,
    onClick,
    children,
  }: {
    disabled: boolean;
    onClick?: () => void;
    children: React.ReactNode;
  }) => (
    <button
      className={classNames(
        'group flex flex-1 items-center justify-center border-t border-outline-variant p-[5px] first:border-t-0'
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );

  return (
    <div className='absolute bottom-16 right-4 z-[18] box-border hidden flex-col items-stretch rounded-[5px] border border-[#6E7979] bg-white p-[2px] shadow-md lg:bottom-11 lg:right-11 lg:flex'>
      <ZoomButton disabled={!canZoomIn} onClick={onZoomIn}>
        <MdAdd
          className={classNames('h-5 w-5 transition-colors', {
            'text-[#506262] group-hover:text-[#002727]': canZoomIn,
            'text-[#181C1C29]': !canZoomIn,
          })}
        />
      </ZoomButton>
      <ZoomButton disabled={!canZoomOut} onClick={onZoomOut}>
        <MdRemove
          className={classNames('h-5 w-5 transition-colors', {
            'text-[#506262] group-hover:text-[#002727]': canZoomOut,
            'text-[#181C1C29]': !canZoomOut,
          })}
        />
      </ZoomButton>
    </div>
  );
}
 */
// Main Calendar Route - using CalendarLayout
export default function CalendarRoute() {
  const { jobId } = useParams();
  const [scrollDate, setScrollDate] = useState<Date>(new Date());

  // Date navigation handlers for layout
  const handleDateNavigate = useCallback(
    (action: 'today' | 'prev' | 'next') => {
      switch (action) {
        case 'today': {
          // Always create a new Date object to ensure React detects the change
          // Add a dynamic millisecond offset to ensure timestamp difference on each click
          const todayDate = startOfDay(new Date());
          todayDate.setMilliseconds(
            todayDate.getMilliseconds() + (Date.now() % 100)
          );
          setScrollDate(todayDate);
          break;
        }
        case 'prev':
          setScrollDate((date) => addDays(startOfDay(date), -7));
          break;
        case 'next':
          setScrollDate((date) => addDays(startOfDay(date), 7));
          break;
      }
    },
    []
  );

  return (
    <CalendarLayout
      scrollDate={scrollDate}
      onDateNavigate={handleDateNavigate}
      showDateNavigation={true}
      showFilterNavArrows={false}
    >
      <HorizontalCalendar selectedJobId={jobId} scrollDate={scrollDate} />
    </CalendarLayout>
  );
}
